import { useEffect, useState } from "react";
import { RefreshCw, Check } from "lucide-react";
import "./avatar-selector.css";

const NUM_AVATARS = 12;
const AVATAR_STYLES = [
  { id: 'notionists', label: 'Notionists' },
  { id: 'lorelei', label: 'Lorelei' },
  { id: 'avataaars', label: 'Avataaars' },
  { id: 'miniavs', label: 'MiniAvs' },
  { id: 'open-peeps', label: 'Open Peeps' },
  { id: 'toon-head', label: 'Toon Head' },
];

// Genera una seed alfanumérica aleatoria
const generateRandomSeed = () => Math.random().toString(36).substring(2, 4) + Math.random().toString(36).substring(2, 4);
const avatarUrl = (style, seed) => `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}`;

/**
 * Elegir avatar (DiceBear): estilo como control segmentado (solo admin: aplica a todo
 * el equipo), el avatar actual grande y una rejilla de 12 opciones con "Otros". Misma
 * API de props que la versión Bootstrap; estilos en avatar-selector.css con las
 * primitivas cp-*.
 */
function AvatarSelector({ selectedStyle = 'notionists', onStyleChange, onAvatarChange, showStyleSelector = true, isAdmin = true, initialSeed = null }) {
  const [seeds, setSeeds] = useState(() => {
    const list = Array(NUM_AVATARS).fill(null).map(() => generateRandomSeed());
    if (initialSeed) list[0] = initialSeed;
    return list;
  });
  const [selectedSeed, setSelectedSeed] = useState(initialSeed || seeds[0]);
  const [currentStyle, setCurrentStyle] = useState(selectedStyle);

  // Reaccionar cuando initialSeed / selectedStyle cambian (ej: al cargar datos desde BD)
  useEffect(() => { if (initialSeed) setSelectedSeed(initialSeed); }, [initialSeed]);
  useEffect(() => { setCurrentStyle(selectedStyle); }, [selectedStyle]);

  useEffect(() => {
    onAvatarChange?.({ seed: selectedSeed, style: currentStyle, url: avatarUrl(currentStyle, selectedSeed) });
  }, [selectedSeed, currentStyle, onAvatarChange]);

  const handleStyleChange = (newStyle) => { setCurrentStyle(newStyle); onStyleChange?.(newStyle); };

  const regenerateAvatars = () => {
    const list = Array(NUM_AVATARS).fill(null).map(() => generateRandomSeed());
    setSeeds(list);
    setSelectedSeed(list[0]);
  };

  return (
    <div className="cp-avsel">
      {showStyleSelector && isAdmin && (
        <div className="cp-avsel-style">
          <span className="cp-avsel-label">Estilo</span>
          <div className="cp-segment-scroll">
            <div className="cp-segment" role="tablist" aria-label="Estilo de avatar">
              {AVATAR_STYLES.map((style) => (
                <button key={style.id} type="button" role="tab" aria-selected={currentStyle === style.id} className={`cp-segment-btn ${currentStyle === style.id ? "is-active" : ""}`} onClick={() => handleStyleChange(style.id)}>
                  {style.label}
                </button>
              ))}
            </div>
          </div>
          <p className="cp-hint">El estilo se aplica a todo el equipo.</p>
        </div>
      )}

      <div className="cp-avsel-current">
        <img src={avatarUrl(currentStyle, selectedSeed)} alt="Tu avatar" />
        <div>
          <span className="cp-avsel-label">Tu avatar</span>
          <p className="cp-hint">Elige otro abajo o pide más opciones.</p>
        </div>
      </div>

      <div className="cp-avsel-grid" role="listbox" aria-label="Opciones de avatar">
        {seeds.map((seed, idx) => {
          const active = selectedSeed === seed;
          return (
            <button key={seed + idx} type="button" role="option" aria-selected={active} className={`cp-avsel-opt ${active ? "is-active" : ""}`} onClick={() => setSelectedSeed(seed)} aria-label={`Avatar opción ${idx + 1}`}>
              <img src={avatarUrl(currentStyle, seed)} alt="" loading="lazy" />
              {active && <span className="cp-avsel-check"><Check size={12} strokeWidth={3} /></span>}
            </button>
          );
        })}
      </div>

      <button type="button" className="cp-btn cp-avsel-more" onClick={regenerateAvatars}>
        <RefreshCw size={15} /> Otras opciones
      </button>
    </div>
  );
}

export default AvatarSelector;
